import { CollectionViewer } from '@angular/cdk/collections';
import { InMemoryDataSource } from '@app/shared/data-sources/in-memory-data-source';
import { Subscription } from 'rxjs';

import { FaxMessage, LocalFaxNumber } from '../models/fax.models';
import { FaxMessageStorageService } from '../services/fax-message-storage.service';

/**
 * Filter options for the FaxMessage DataSource.
 */
export interface FaxMessageFilterOptions {
  /** Search term to filter by contact name or phone number */
  searchTerm?: string;

  /** Filter by direction (inbound/outbound) */
  direction?: FaxMessage['direction'];

  /** Filter by local fax number */
  localNumber?: LocalFaxNumber;

  /** Whether 'All' local numbers is selected */
  isAllLocalNumber?: boolean;
}

/**
 * DataSource for FaxMessage backed by in-memory cache.
 *
 * Subscribes to storageService.bulkDataChanged$ and calls refresh() on every change,
 * which re-reads the (already-updated) in-memory cache and emits the filtered snapshot.
 */
export class FaxMessageDataSource extends InMemoryDataSource<FaxMessage> {
  private filterOptions: FaxMessageFilterOptions = {};

  private searchResultIds: string[] | null = null;

  private bulkDataChangedSubscription?: Subscription;

  constructor(private storageService: FaxMessageStorageService) {
    super();

    this.refresh();

    this.bulkDataChangedSubscription = this.storageService.bulkDataChanged$.subscribe(() => this.refresh());
  }

  public override disconnect(collectionViewer: CollectionViewer): void {
    super.disconnect(collectionViewer);
    this.bulkDataChangedSubscription?.unsubscribe();
  }

  /**
   * Set filter options and refresh the data.
   */
  public setFilterOptions(options: FaxMessageFilterOptions): void {
    const changed =
      this.filterOptions.direction !== options.direction ||
      this.filterOptions.localNumber?.number !== options.localNumber?.number ||
      this.filterOptions.isAllLocalNumber !== options.isAllLocalNumber ||
      this.filterOptions.searchTerm !== options.searchTerm;

    if (!changed) return;

    this.filterOptions = options;

    const { searchTerm } = options;
    if (searchTerm && searchTerm.length > 0) {
      this.searchResultIds = this.storageService.search(searchTerm);
    } else {
      this.searchResultIds = null;
    }

    this.refresh();
  }

  /**
   * Get the current filter options.
   */
  public getFilterOptions(): FaxMessageFilterOptions {
    return { ...this.filterOptions };
  }

  /**
   * Rebuild the full filtered snapshot from the in-memory cache and emit it.
   */
  public refresh(): void {
    const { searchTerm } = this.filterOptions;
    if (searchTerm && searchTerm.length > 0) {
      this.searchResultIds = this.storageService.search(searchTerm);
    }

    this.emitData(this.getFilteredItems());
  }

  // ========== Private helper methods ==========

  private getFilteredItems(): FaxMessage[] {
    if (this.searchResultIds !== null) {
      let items = this.storageService.getByIds(this.searchResultIds);
      const runtimeFilter = this.buildRuntimeFilterFunction();
      if (runtimeFilter) items = items.filter(runtimeFilter);
      return items;
    }

    const runtimeFilter = this.buildRuntimeFilterFunction();
    if (runtimeFilter) {
      return this.storageService.getFilteredItems(runtimeFilter);
    }
    return this.storageService.getAllSortedItems();
  }

  private buildRuntimeFilterFunction(): ((item: FaxMessage) => boolean) | undefined {
    const { direction, localNumber, isAllLocalNumber } = this.filterOptions;

    if (!direction && (isAllLocalNumber || !localNumber)) {
      return undefined;
    }

    return (item: FaxMessage) => {
      if (direction && item.direction !== direction) return false;

      if (!isAllLocalNumber && localNumber) {
        const numberToCheck = item.direction === 'inbound' ? item.destination : item.caller_id;
        if (String(numberToCheck) !== String(localNumber.number)) return false;
      }

      return true;
    };
  }
}
